// A list of tasks (BL-004) and the form that adds one: on Inbox › Tasks, a customer's history and a
// job's sheet. Each task says what needs doing, when it's due, its type, who has it and what it's
// about; Done closes it (with a note), and it can be moved to in progress or waiting, changed, or a
// closed one reopened. The server checks every change against the version this list read.

import { useState } from 'react';
import { today as localToday } from '../book-model.ts';
import { customerHref } from '../history/customer-link.ts';
import { useAct, useQuery } from '../books/ui.tsx';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';
import { Loading } from '../shared/Loading.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import {
  closedText,
  createInput,
  draftProblem,
  dueWords,
  grouped,
  isOpen,
  STATUS_LABEL,
  taskMeta,
  tasksInput,
  type Task,
  type TaskPage,
  type TaskDraft,
  type TaskType,
} from './tasks-model.ts';
import { useDesign } from '../blueprint/design.ts';
import './tasks.css';

export interface TaskQuery {
  assignee?: 'me' | 'unassigned' | string;
  state?: 'open' | 'closed' | 'all';
  customerId?: string;
  jobId?: string;
}

export function TaskList({
  title,
  query,
  about = {},
  timeZone,
  canAdd,
  groupByDue = false,
  showAbout = 'all',
  empty,
  defaultAssignee,
  onOpenJob,
  onChanged,
}: {
  /** The section's name, as a screen reader hears it ("Your tasks", "Tasks for this job"). */
  title: string;
  query: TaskQuery;
  /** What a task added here is about. */
  about?: { customerId?: string; jobId?: string };
  timeZone: string;
  canAdd: boolean;
  /** Inbox: in groups by when they're due. */
  groupByDue?: boolean;
  /** What each task says it's about: all of it, or not what the place already is (a customer's record, a job's sheet). */
  showAbout?: 'all' | 'job' | 'none';
  empty: string;
  /** A task added here goes to this person unless the form says otherwise (Yours: the person's own). */
  defaultAssignee?: string;
  /** Opens a task's job (the job sheet); without it the job is named, not a link. */
  onOpenJob?: (jobId: string) => void;
  onChanged?: () => void;
}) {
  const [said, setSaid] = useState('');
  // a page at a time, "Load more" reading on (list caps, BL-110): an office's open tasks pass a page
  const list = usePaged<Task, TaskPage>('tasks.list', tasksInput({ ...query }), (p) => p.tasks);
  const act = useAct(setSaid, () => {
    list.reload();
    onChanged?.();
  });
  const [adding, setAdding] = useState(false);
  const bp = useDesign() === 'blueprint';
  const today = localToday(timeZone);
  if (!list.rows) return <Loading error={list.error} what="the tasks" retry={list.reload} />;
  const tasks = list.rows;
  const total = list.first?.total ?? tasks.length;
  const row = (t: Task) => (
    <TaskRow
      key={t.id}
      task={t}
      today={today}
      timeZone={timeZone}
      showAbout={showAbout}
      act={act}
      {...(onOpenJob ? { onOpenJob } : {})}
    />
  );
  return (
    <div className="task-list">
      {canAdd &&
        (adding ? (
          <TaskForm
            about={about}
            assignee={defaultAssignee ?? ''}
            onDone={async (said) => {
              setAdding(false);
              if (said) {
                setSaid(said);
                list.reload();
                onChanged?.();
              }
            }}
          />
        ) : (
          <button className={bp ? 'ib-link task-add' : 'btn soft task-add'} onClick={() => setAdding(true)}>
            Add a task
          </button>
        ))}
      {!tasks.length ? (
        <p className="muted empty">{empty}</p>
      ) : groupByDue && query.state !== 'closed' ? (
        grouped(tasks, today).map((g) =>
          bp ? (
            // Blueprint: a group inside the section is divided by a hairline, never a section of its own
            <div
              key={g.group}
              className="task-group"
              role="group"
              aria-label={`${title}: ${g.group.toLowerCase()}`}
            >
              <h4 className={g.group === 'Overdue' ? 'bad-ink' : undefined}>
                {g.group} <span className="muted">{g.tasks.length}</span>
              </h4>
              <ul className="tasks">{g.tasks.map(row)}</ul>
            </div>
          ) : (
            <section key={g.group} className="task-group" aria-label={`${title}: ${g.group.toLowerCase()}`}>
              <h3 className={g.group === 'Overdue' ? 'bad-ink' : undefined}>
                {g.group} <span className="muted">{g.tasks.length}</span>
              </h3>
              <ul className="tasks">{g.tasks.map(row)}</ul>
            </section>
          ),
        )
      ) : (
        <ul className="tasks" aria-label={title}>
          {tasks.map(row)}
        </ul>
      )}
      {(list.more || list.busy) && (
        <p className="task-more">
          <span className="muted small">{shownText(tasks.length, total)}</span>{' '}
          <button
            type="button"
            className={bp ? 'ib-link' : 'btn soft'}
            disabled={list.busy}
            onClick={() => list.more?.()}
          >
            {list.busy ? 'Loading…' : 'Load more'}
          </button>
        </p>
      )}
      {list.error && tasks.length > 0 && (
        <p className="error" role="alert">
          {list.error}
        </p>
      )}
      <p className="task-said" role="status">
        {said}
      </p>
    </div>
  );
}

function TaskRow({
  task: t,
  today,
  timeZone,
  showAbout,
  act,
  onOpenJob,
}: {
  task: Task;
  today: string;
  timeZone: string;
  showAbout: 'all' | 'job' | 'none';
  act: ReturnType<typeof useAct>;
  onOpenJob?: (jobId: string) => void;
}) {
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  // Blueprint's row: Done is its one action; the rest are underlined words
  const bp = useDesign() === 'blueprint';
  const due = dueWords(t, today, timeZone);
  const customer = showAbout === 'all' ? t.customer : null;
  const job = showAbout === 'none' ? null : t.job;
  const open = isOpen(t);
  return (
    <li className={`task${t.overdue ? ' task-late' : ''}${open ? '' : ' closed'}`}>
      <div className="task-main">
        <b className="task-title">{t.title}</b>
        {due && <span className={`chip${t.overdue ? ' bad' : ''}`}>{due}</span>}
      </div>
      <p className="muted small task-meta">{taskMeta(t)}</p>
      {(customer || job) && (
        <p className="small task-about">
          {customer && <a href={customerHref(customer.id)}>{customer.name}</a>}
          {customer && job && ' · '}
          {job &&
            (onOpenJob ? (
              <button className="cell-link" onClick={() => onOpenJob(job.id)}>
                Job #{job.number}
              </button>
            ) : (
              <span>Job #{job.number}</span>
            ))}
        </p>
      )}
      {t.details && <p className="small task-details">{t.details}</p>}
      {!open && <p className="small muted">{closedText(t)}</p>}
      {editing ? (
        <TaskEdit
          task={t}
          onDone={async (input) => {
            setEditing(false);
            if (input) await act.run('tasks.update', input, 'Task changed.');
          }}
        />
      ) : closing ? (
        <form
          className="task-close"
          aria-label={`Close “${t.title}”`}
          onSubmit={(e) => {
            e.preventDefault();
            void act
              .run(
                'tasks.close',
                {
                  id: t.id,
                  rowVersion: t.rowVersion,
                  outcome: 'done',
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                'Task done.',
              )
              .then(() => setClosing(false));
          }}
        >
          <label>
            How it ended (optional)
            <input
              className="field"
              value={note}
              maxLength={1000}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="row-actions">
            <button className="btn primary" type="submit" disabled={act.busy}>
              Done
            </button>
            <button
              className={bp ? 'ib-link' : 'btn soft'}
              type="button"
              disabled={act.busy}
              onClick={() =>
                void act
                  .run(
                    'tasks.close',
                    {
                      id: t.id,
                      rowVersion: t.rowVersion,
                      outcome: 'cancelled',
                      ...(note.trim() ? { note: note.trim() } : {}),
                    },
                    'Task cancelled.',
                  )
                  .then(() => setClosing(false))
              }
            >
              Not needed
            </button>
            <button className={bp ? 'ib-link' : 'btn quiet'} type="button" onClick={() => setClosing(false)}>
              Back
            </button>
          </div>
        </form>
      ) : (
        <div className="row-actions task-actions">
          {open ? (
            <>
              <button
                className={bp ? 'ib-link' : 'btn soft small'}
                aria-label={`Done: ${t.title}`}
                onClick={() => setClosing(true)}
                disabled={act.busy}
              >
                Done…
              </button>
              <label className="task-status">
                <span className="sr-only">State of “{t.title}”</span>
                <select
                  className="field"
                  value={t.status}
                  disabled={act.busy}
                  onChange={(e) =>
                    void act.run(
                      'tasks.update',
                      { id: t.id, rowVersion: t.rowVersion, status: e.target.value },
                      `Task set to ${STATUS_LABEL[e.target.value as Task['status']].toLowerCase()}.`,
                    )
                  }
                >
                  {(['open', 'in_progress', 'waiting'] as const).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className={bp ? 'ib-link' : 'btn quiet small'}
                aria-label={`Change “${t.title}”`}
                onClick={() => setEditing(true)}
              >
                Change
              </button>
            </>
          ) : (
            <button
              className={bp ? 'ib-link' : 'btn soft small'}
              aria-label={`Reopen “${t.title}”`}
              disabled={act.busy}
              onClick={() =>
                void act.run(
                  'tasks.update',
                  { id: t.id, rowVersion: t.rowVersion, status: 'open' },
                  'Task reopened.',
                )
              }
            >
              Reopen
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function useTypesAndStaff() {
  const types = useQuery<{ types: TaskType[] }>('taskTypes.list', {});
  const staff = useQuery<{ staff: { id: string; displayName: string; isTechnician: boolean }[] }>(
    'staff.list',
    {},
  );
  return { types: types.data?.types ?? null, staff: staff.data?.staff ?? [], error: types.error };
}

function DraftFields({
  draft,
  set,
  types,
  staff,
}: {
  draft: TaskDraft;
  set: (d: Partial<TaskDraft>) => void;
  types: TaskType[];
  staff: { id: string; displayName: string }[];
}) {
  return (
    <>
      <label>
        Kind
        <select className="field" value={draft.typeCode} onChange={(e) => set({ typeCode: e.target.value })}>
          {!draft.typeCode && <option value="">Pick one</option>}
          {types.map((t) => (
            <option key={t.code} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="full">
        What needs doing
        <input
          className="field"
          value={draft.title}
          maxLength={200}
          onChange={(e) => set({ title: e.target.value })}
        />
      </label>
      <label>
        Due
        <input
          className="field"
          type="date"
          value={draft.dueOn}
          onChange={(e) => set({ dueOn: e.target.value })}
        />
      </label>
      <label>
        Who
        <select
          className="field"
          value={draft.assigneeStaffId}
          onChange={(e) => set({ assigneeStaffId: e.target.value })}
        >
          <option value="">Nobody yet</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.displayName}
            </option>
          ))}
        </select>
      </label>
      <label>
        Priority
        <select
          className="field"
          value={draft.priority}
          onChange={(e) => set({ priority: e.target.value as TaskDraft['priority'] })}
        >
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
      </label>
      <label className="full">
        Details (optional)
        <textarea
          className="field"
          rows={2}
          value={draft.details}
          maxLength={2000}
          onChange={(e) => set({ details: e.target.value })}
        />
      </label>
    </>
  );
}

const BLANK: TaskDraft = {
  typeCode: '',
  title: '',
  details: '',
  dueOn: '',
  priority: 'normal',
  assigneeStaffId: '',
};

function TaskForm({
  about,
  assignee,
  onDone,
}: {
  about: { customerId?: string; jobId?: string };
  assignee: string;
  onDone: (said: string | null) => Promise<void>;
}) {
  const { types, staff, error } = useTypesAndStaff();
  const [draft, setDraft] = useState<TaskDraft>({ ...BLANK, assigneeStaffId: assignee });
  const [why, setWhy] = useState<string | null>(null);
  const act = useAct(setWhy, () => undefined);
  const key = useSaveKey(JSON.stringify(draft));
  if (!types) return <Loading error={error} what="the task types" />;
  const save = async () => {
    const problem = draftProblem(draft);
    if (problem) return setWhy(problem);
    const r = await act.run('tasks.create', createInput(key.key, draft, about), undefined, {
      idempotencyKey: key.key,
    });
    if (r) {
      key.renew();
      await onDone('Task added.');
    }
  };
  return (
    <form
      className="task-form"
      aria-label="Add a task"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <DraftFields draft={draft} set={(d) => setDraft({ ...draft, ...d })} types={types} staff={staff} />
      <div className="row-actions full">
        <SaveButton busy={act.busy} busyLabel="Adding…" error={why} onClick={() => void save()}>
          Add the task
        </SaveButton>
        <button className="btn quiet" type="button" onClick={() => void onDone(null)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function TaskEdit({
  task: t,
  onDone,
}: {
  task: Task;
  onDone: (input: Record<string, unknown> | null) => Promise<void>;
}) {
  const { types, staff, error } = useTypesAndStaff();
  const [draft, setDraft] = useState<TaskDraft>({
    typeCode: t.type.code,
    title: t.title,
    details: t.details ?? '',
    dueOn: t.dueOn ?? '',
    priority: t.priority,
    assigneeStaffId: t.assignee?.id ?? '',
  });
  const [why, setWhy] = useState<string | null>(null);
  if (!types) return <Loading error={error} what="the task types" />;
  // a type switched off since stays pickable for the task that has it
  const shown = types.some((y) => y.code === t.type.code)
    ? types
    : [...types, { code: t.type.code, name: t.type.name, active: false }];
  return (
    <form
      className="task-form"
      aria-label={`Change “${t.title}”`}
      onSubmit={(e) => {
        e.preventDefault();
        const problem = draftProblem(draft);
        if (problem) return setWhy(problem);
        void onDone({
          id: t.id,
          rowVersion: t.rowVersion,
          typeCode: draft.typeCode,
          title: draft.title.trim(),
          details: draft.details.trim() || null,
          dueOn: draft.dueOn || null,
          priority: draft.priority,
          assigneeStaffId: draft.assigneeStaffId || null,
        });
      }}
    >
      <DraftFields draft={draft} set={(d) => setDraft({ ...draft, ...d })} types={shown} staff={staff} />
      <div className="row-actions full">
        <button className="btn primary" type="submit">
          Save
        </button>
        <button className="btn quiet" type="button" onClick={() => void onDone(null)}>
          Cancel
        </button>
        {why && (
          <span className="save-why" role="alert">
            {why}
          </span>
        )}
      </div>
    </form>
  );
}
